import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  emailValidator,
  emailWOValueValidator,
  gstWOValueValidator,
  MobileNoValidator,
  panWOValueValidator,
  pincodeWOValueValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import {
  createOrganization,
  getOrganizationByID,
  organizationLogoUpload,
  updateOrganization,
} from "./services/organizationRegister.services";
import {
  clearOrganizationDetails,
  setSoftwareSettingDetail,
} from "./organizationRegisterSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import ShadowCard from "../../components/common/ShadowCard";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { openDialog } from "../../components/dialog/dialogSlice";
import { MdSettings } from "react-icons/md";
import IconComponent from "../../components/common/IconComponent";
import SoftwareSettingDialog from "./SoftwareSettingDialog";

const CreateOrganizationRegister: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const OrganizationID = location.state?.OrganizationID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.organizationRegister.loading);
  const OrganizationDetail = useAppSelector(
    (state) => state.organizationRegister.OrganizationDetail,
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (OrganizationID) {
      dispatch(getOrganizationByID(OrganizationID));
    }
    return () => {
      dispatch(clearOrganizationDetails());
    };
  }, [OrganizationID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [OrganizationDetail]);

  const handleOpenSettingDialog = (data: any) => {
    dispatch(openDialog("softwareSettingDialog"));
    dispatch(setSoftwareSettingDetail(data));
  };

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const file =
      values?.LogoImage && typeof values?.LogoImage !== "string"
        ? values.LogoImage[0].getRawFile()
        : "";

    const payload = {
      ...(OrganizationID ? { ID: OrganizationID } : {}),
      OrganizationName: values.OrganizationName ? values.OrganizationName : "",
      LegalName: values.LegalName ? values.LegalName : "",
      GSTNumber: values.GSTNumber ? values.GSTNumber : "",
      PANNumber: values.PANNumber ? values.PANNumber : "",
      Email: values.Email ? values.Email : "",
      Phone: values.Phone ? values.Phone?.toString() : "",
      Website: values.Website ? values.Website : "",
      AddressLine1: values.AddressLine1 ? values.AddressLine1 : "",
      AddressLine2: values.AddressLine2 ? values.AddressLine2 : "",
      City: values.City ? values.City : "",
      State: values.State ? values.State : "",
      Country: values.Country ? values.Country : "",
      Pincode: values.Pincode ? values.Pincode?.toString() : "",
      CurrencyCode: values.CurrencyCode ? values.CurrencyCode : "",
      ContactPerson: values.ContactPerson ? values.ContactPerson : "",
      ContactMobile: values.ContactMobile
        ? values.ContactMobile?.toString()
        : "",
      ContactEmail: values.ContactEmail ? values.ContactEmail : "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (OrganizationID) {
        const response = await dispatch(updateOrganization(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values.LogoImage && typeof values.LogoImage !== "string") {
            ImageData.append("LogoImage", file);
            ImageData.append("ID", OrganizationID.toString());

            await dispatch(organizationLogoUpload(ImageData));
          }
          navigate("/organization");
        }
      } else {
        const response = await dispatch(createOrganization(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          const newID =
            response?.payload?.Data?.ID ||
            response?.payload?.Data?.OrganizationID ||
            response?.payload?.ID;
          if (
            values?.LogoImage &&
            typeof values?.LogoImage !== "string" &&
            newID
          ) {
            ImageData.append("LogoImage", file);
            ImageData.append("ID", newID.toString());

            await dispatch(organizationLogoUpload(ImageData));
          }
          navigate(-1);
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <SoftwareSettingDialog />
      <Form
        onSubmit={handleSubmit}
        initialValues={OrganizationDetail}
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem
                  colSpan={3}
                  className="d-flex justify-content-between align-items-center gap-2"
                >
                  <Typography.h4>
                    {OrganizationID
                      ? "Update Organization"
                      : "Add Organization"}
                  </Typography.h4>
                  {OrganizationID && (
                    <Button
                      type="button"
                      title="Software Setting"
                      themeColor={"primary"}
                      fillMode={"flat"}
                      className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() =>
                        handleOpenSettingDialog(OrganizationDetail)
                      }
                    >
                      <IconComponent icon={MdSettings} className="fs-4" />
                    </Button>
                  )}
                </GridLayoutItem>

                {/* Basic Details */}
                <GridLayoutItem>
                  <Field
                    name="OrganizationName"
                    maxLength="100"
                    label="Organization Name"
                    placeholder="Organization Name"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="LegalName"
                    maxLength="150"
                    label="Legal Name"
                    placeholder="Legal Name"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Email"
                    label="Email ID"
                    placeholder="i.e. info@domain.com"
                    component={FormTextField}
                    astrike={true}
                    validator={emailValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Phone"
                    type="number"
                    placeholder="i.e. 9876543210"
                    label="Phone"
                    max="16"
                    component={FormTextField}
                    validator={MobileNoValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Website"
                    label="Website"
                    placeholder="i.e. https://www.website.com"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="GSTNumber"
                    label="GST Number"
                    placeholder="GST Number"
                    validator={gstWOValueValidator}
                    component={FormTextField}
                    maxLength={15}
                    onChange={(e) =>
                      formRenderProps.onChange("GSTNumber", {
                        value: e?.value?.toUpperCase(),
                      })
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="PANNumber"
                    label="PAN Number"
                    placeholder="PAN Number"
                    validator={panWOValueValidator}
                    component={FormTextField}
                    maxLength={10}
                    onChange={(e) =>
                      formRenderProps.onChange("PANNumber", {
                        value: e?.value?.toUpperCase(),
                      })
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="CurrencyCode"
                    label="Currency Code"
                    placeholder="i.e. INR"
                    component={FormSelectionField}
                    options={[
                      { label: "INR", value: "INR" },
                      { label: "USD", value: "USD" },
                      { label: "EUR", value: "EUR" },
                      { label: "GBP", value: "GBP" },
                    ]}
                  />
                </GridLayoutItem>

                {/* Address Details */}
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="AddressLine1"
                    label="Address Line 1"
                    placeholder="Address Line 1"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="AddressLine2"
                    label="Address Line 2"
                    placeholder="Address Line 2"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="City"
                    label="City"
                    placeholder="City"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="State"
                    label="State"
                    placeholder="State"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Country"
                    label="Country"
                    placeholder="Country"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Pincode"
                    type="number"
                    placeholder="Pincode"
                    label="Pincode"
                    max={6}
                    component={FormTextField}
                    validator={pincodeWOValueValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    id={"IsActive"}
                    name={"IsActive"}
                    label={"Status"}
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status.value,
                        label: status.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  {/* Empty item to balance layout */}
                </GridLayoutItem>

                {/* Contact Person Details */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h5
                    style={{
                      textDecoration: "underline",
                      margin: "20px 0 0 0",
                    }}
                  >
                    Contact Person Details
                  </Typography.h5>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="ContactPerson"
                    label="Contact Person Name"
                    placeholder="Contact Person Name"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="ContactMobile"
                    type="number"
                    placeholder="Contact Mobile No"
                    label="Contact Mobile"
                    max="16"
                    component={FormTextField}
                    validator={MobileNoValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="ContactEmail"
                    label="Contact Email ID"
                    placeholder="Contact Email ID"
                    component={FormTextField}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>

                {/* Logo Image Upload */}
                <GridLayoutItem colSpan={3} style={{ marginTop: "15px" }}>
                  <Field
                    name="LogoImage"
                    label="Organization Logo"
                    alternateName="Organization Logo"
                    type="file"
                    accept="image/*"
                    component={FormImageUpload}
                    maxSize={0.5}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={OrganizationID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />

                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/organization")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateOrganizationRegister;
